import SimpleStore from './simpleStore'
import { Roles, UserRoles } from 'constants/roles'
import logger from 'utils/logger'
import { SYMBOLS_BY_CURRENCY } from 'constants/symbolsByCurrency'
import { CurrencyInfo } from 'types/interfaces'
import i18n, { getEffectiveLocale } from '../i18n'

const simpleStore = new SimpleStore()
const USER_COOKIE_NAME = 'UNBRIDALED_USER'
interface DiamondProcessResponse {
  success: boolean
  error?: {
    errorTitle: string | null
    errorBody: string | null
    errorCode?: string | null
  }
}

type addToCartItem = {
  id: number
  quantity: number
  properties: {
    Certificate?: string
    "Ring Size"?: string
    _grouping?: string
  }    
}
class StoreFunctions {
  private sleep(ms: number): Promise<void> {
    return new Promise<void>((resolve: () => void): void => {
      setTimeout(resolve, ms)
    })
  }

  private async areVariantsAvailableInStore(variantIds: number[]): Promise<boolean> {
    if (!variantIds.length) return false
    try {
      const results = await Promise.all(
        variantIds.map(id => simpleStore.ajax('GET', `/variants/${id}.js`))
      )
      return results.every(result => result?.available === true)
    } catch (e) {
      logger.info(e)
      return false
    }
  }

  private async buildDiamondProducts(
    gems: { [key: string]: any }[],
    searchType: string,
    stoneType: string
  ): Promise<{
    products: { [key: string]: any }[]
    unprocessedSkus: string[]
  }> {
    const products: { [key: string]: any }[] = []
    const unprocessedSkus: string[] = []
    for (const gem of gems) {
      try {
        const diamondVariant = await this.createProduct(gem, 'i', searchType, stoneType)
        // createProduct resolves to null (rather than throwing) when ds/generate fails to
        // mint a variant, so a falsy result must be routed to unprocessedSkus explicitly —
        // otherwise it silently becomes a products entry keyed by the string "undefined",
        // which downstream resolves to a bogus variant id of 0 and 422s on /cart/add.js.
        if (!diamondVariant?.id) {
          unprocessedSkus.push(gem.sku)
          continue
        }
        products.push({
          [diamondVariant.id]: gem
        })
      } catch (e) {
        logger.info(e)
        unprocessedSkus.push(gem.sku)
      }
    }

    return {
      products,
      unprocessedSkus
    }
  }

  private async checkDiamondInCart(diamonds: { [key: string]: any }[]): Promise<{ sku: string }[]> {
    const result: { sku: string }[] = []
    const skus = diamonds.map(diamond => diamond.sku)
    const cartItems = await simpleStore.ajax('GET', '/cart.js')
    if (!cartItems?.items?.length) return result

    for (const item of cartItems.items) {
      if (skus.includes(item.sku)) {
        result.push({ sku: item.sku })
      }
    }

    return result
  }

  async getProductsForSKU(
    productIds: string[] | boolean
  ): Promise<{ [key: string]: any }[] | null> {
    try {
      return await simpleStore.ajax(
        'POST',
        'get_products_for_diamond',
        JSON.stringify({
          productIds: productIds
        })
      )
    } catch (e) {
      logger.info('error getProducts:' + e)
      return null
    }
  }

  async tagProductsForSKU(
    diamondsData: { [key: string]: any }[],
    mode = 'single'
  ): Promise<string[] | boolean> {
    try {
      const data = await simpleStore.ajax(
        'POST',
        'find_products_for_diamond',
        JSON.stringify({
          diamondsData,
          mode
        })
      )
      if (data.length) {
        return data
      }
      return false
    } catch (e) {
      logger.info(e)
      logger.info('error retrieveProducts:' + JSON.stringify(e))
      return false
    }
  }

  translateShapeAPINameToIcon(props: { [key: string]: any }): string | undefined {
    const shapeUrl = props.shape_image_url || props.shape_icon_url || props.shape_icon_url_png
    if (shapeUrl) {
      return `<img src=${shapeUrl} alt=${props.shape} />`
    }
  }

  dispatchEvent(eventName: string, evtDetail?: any): void {
    let evt: Event

    const body = document.getElementsByTagName('body')[0]
    if (!evtDetail) {
      evt = new Event(eventName)
    } else {
      try {
        evt = new CustomEvent(eventName, { detail: evtDetail })
      } catch (e) {
        evt = document.createEvent('Event')
        evt.initEvent(eventName, true, true)
        ;(evt as any).detail = evtDetail
      }
    }
    if (!body) return
    body.dispatchEvent(evt)
  }

  async getVariantById(variantId: string): Promise<{ [key: string]: any } | null> {
    try {
      const result = await simpleStore.ajax('GET', `ds/variants/${variantId}`)
      return result;
    } catch (e) {
      logger.info('error getVariantById:' + e)
      return null
    }
  }

  async getShopifyProductData(productId: string): Promise<{ [key: string]: any } | null> {
    try {
      const result = await simpleStore.ajax('GET', `ds/shopify_product_data/${productId}`)
      return result;
    } catch (e) {
      logger.info('error getShopifyProductData:' + e)
      return null
    }
  }

  async createProduct(
    diamond: { [key: string]: any },
    type = 'i',
    searchType: string,
    stoneType = "diamond"
  ): Promise<{ [key: string]: any } | null> {
    const { diamond_image_bottom_url, diamond_image_top_url } = diamond

    if (diamond_image_bottom_url) {
      diamond.diamond_image_url = diamond_image_bottom_url
    } else if (diamond_image_top_url) {
      diamond.diamond_image_url = diamond_image_top_url
    }

    if (typeof diamond.retail_price === 'string') {
      diamond.retail_price = +diamond.retail_price.replace(/[\,]/g, '')
    }
    try {
      const query = new URLSearchParams({
        sku: diamond.sku,
        stone_type: stoneType,
        search_type: searchType,
        t: type,
      })
      const data = await simpleStore.ajax('GET', `ds/generate/?${query.toString()}`)
      if (data.variant !== null) return data.variant
      throw data.error || 'unexpected error'
    } catch (e) {
      logger.info(e)
      return null
    }
  }

  sendUrgentEmail = async (shop: string, merchantUUID: string, type: string, items: { id: string; sku: string }[] | { id: number; sku: string }[], errorMessage: string): Promise<{ success: boolean; error?: { errorTitle: string; errorBody: string } }> => {
    try {
      const developerEmailResult = await this.sendEmail(
          "developer@unbridaled.diamonds",
          `Urgent: error placing Unbridaled order`,
          `<p>Shopify domain: ${shop}</p>` +
            `<p>Merchant UUID: ${merchantUUID}</p>` +
            `<p>Order type: ${type}</p>` +
            `<p>${items.length > 1 ? "SKUs: " : "SKU: "}${items.map((item) => item.sku).join(", ")}</p>` +
            `<p>Error: ${errorMessage}</p>`,
          "support@unbridaled.ai, orders@unbridaled.ai",
        )
        const customerEmailResult = await this.sendEmail(
          null,
          'Urgent: error placing Unbridaled order',
          `Dear Customer,\n\nThere was an error in placing your Unbridaled order\n\nError: ${errorMessage}\n\nSKUs: ${items.map((item) => item.sku).join(", ")}\n\nTo not loose your order, please get in touch with Unbridaled Support\n\nUnbridaled AI`,
          undefined,
          undefined,
          shop
        )
      if (developerEmailResult.success && customerEmailResult.success) {
        return {
          success: true
        }
      }
      return {
        success: false,
        error: {
          errorTitle: i18n.t('errors.emailError'),
          errorBody: developerEmailResult.error || customerEmailResult.error || i18n.t('errors.failedToSendEmail')
        }
      }
    } catch (e) {
      logger.info(e)
      return {
        success: false,
        error: {
          errorTitle: i18n.t('errors.emailError'),
          errorBody: (e as Error).message || i18n.t('errors.failedToSendEmail')
        }
      }
    }
  }

  doPlaceOrder = async (
    diamondProductsData: { [key: string]: any }[],
    type: string,
    shop: string
  ): Promise<DiamondProcessResponse> => {
    const items = diamondProductsData.flatMap(diamondProduct => {
      const id = Object.keys(diamondProduct)[0]
      if (!id) return []
      const diamond = diamondProduct[id]
      if (!diamond?.gemData?.sku) return []
      const matchResult = id.toString().match(/\d+$/)
      return [{
        id: matchResult ? matchResult[0] : '',
        sku: diamond.gemData.sku,
        type,
        quantity: 1
      }]
    })

    if (items.length !== diamondProductsData.length) {
      return {
        success: false,
        error: {
          errorTitle: i18n.t('errors.invalidOrderData'),
          errorBody: i18n.t('errors.invalidOrderDataBody')
        }
      }
    }

    const userData = JSON.parse(localStorage.getItem(USER_COOKIE_NAME) || '{}')
    try {
      const data = await simpleStore.ajax('POST', 'placetodas', JSON.stringify({ items, userData }))
      if (!data.success) {
        const serverMessage = data?.info?.error_message
        // The urgent email goes to the team, not the shopper, so it stays in English.
        const errorMessage = serverMessage || i18n.t('errors.anErrorOccurred')
        // Try to send urgent email, but don't let email failures mask the original error
        try {
          const emailResult = await this.sendUrgentEmail(shop, userData?.merchant_uuid || '', type, items.map((item: { id: string; sku: string }) => ({ id: parseInt(item.id), sku: item.sku })), serverMessage || i18n.t('errors.anErrorOccurred', { lng: 'en' }))
          if (!emailResult.success) {
            logger.info('Failed to send urgent email notification:', emailResult.error)
          }
        } catch (emailError) {
          logger.info('Exception while sending urgent email notification:', emailError)
        }
        // Always return the original order placement error
        return {
          success: false,
          error: {
            errorTitle: i18n.t('errors.anErrorOccurred'),
            errorBody: errorMessage
          }
        }
      }
      return {
        success: true
      }
    } catch (e) {
      const errorMessage = (e as Error).message || i18n.t('errors.anErrorOccurred')
      logger.info('Order placement exception:', e)
      // Try to send urgent email, but don't let email failures mask the original error
      try {
        const emailResult = await this.sendUrgentEmail(shop, userData?.merchant_uuid || '', type, items.map((item: { id: string; sku: string }) => ({ id: parseInt(item.id), sku: item.sku })), (e as Error).message || i18n.t('errors.anErrorOccurred', { lng: 'en' }))
        if (!emailResult.success) {
          logger.info('Failed to send urgent email notification:', emailResult.error)
        }
      } catch (emailError) {
        logger.info('Exception while sending urgent email notification:', emailError)
      }
      // Always return the original exception error
      return {
        success: false,
        error: {
          errorTitle: i18n.t('errors.orderProcessError'),
          errorBody: errorMessage
        }
      }
    }
  }

  addToDAS = async (params: { [key: string]: any }): Promise<DiamondProcessResponse> => {
    try {
      let errorTitle = null
      let errorBody = null
      const gems = Array.isArray(params.diamonds) ? params.diamonds : [params.diamonds]
      const availabilityResult = await this.gemsAvailable(gems, params.searchType)
      if (!availabilityResult.isAvailable) {
        errorTitle = availabilityResult.error
        errorBody = availabilityResult.errorBody
      } else {
        const products = []
        const unprocessedSkus = []
        for (const gem of availabilityResult.gems) {
          try {
            const diamondVariant = await this.createProduct(gem, params.type, params.searchType)
            products.push({
              [diamondVariant?.id]: {
                gemData: gem
              }
            })
          } catch (e) {
            logger.info(e)
            unprocessedSkus.push(gem.sku)
          }
        }
        if (unprocessedSkus.length != 0) {
          errorTitle = i18n.t('errors.createProductsError')
          errorBody = i18n.t('errors.unableToCreateProductsFor', { skus: unprocessedSkus.join(', ') })
        } else {
          return await this.doPlaceOrder(products, params.type, params.shop)
        }
      }
      return {
        success: false,
        error: {
          errorTitle,
          errorBody
        }
      }
    } catch (e) {
      logger.info(e)
      const errorMessage = e instanceof Error ? e.message : String(e)
      return {
        success: false,
        error: {
          errorTitle: i18n.t('errors.orderProcessError'),
          errorBody: errorMessage
        }
      }
    }
  }

  formatPrice = (value?: number | null, currencyInfo: CurrencyInfo = { currency: 'USD', currencyRate: 1, currencies: [] }): string => {
    const manuallyMappedCurrencies = ['CAD']
    if (value) {
      const symbol = currencyInfo.currency ? SYMBOLS_BY_CURRENCY[currencyInfo.currency as keyof typeof SYMBOLS_BY_CURRENCY] : null
      let price = Intl.NumberFormat(getEffectiveLocale(), {
        style: !currencyInfo.currency || manuallyMappedCurrencies.includes(currencyInfo.currency) ? undefined : 'currency',
        currency: currencyInfo.currency ?? undefined,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(value)
      if (currencyInfo.currency === 'CAD') price = `${symbol}${price} CAD`
      return price
    } else {
      return ''
    }
  }

  gemsAvailable = async (diamonds: { [key: string]: any }[], searchType: string): Promise<any> => {
    let isAvailable = false
    let error = null
    let errorBody = null
    let gems = null

    try {
      const skus = diamonds.map(gem => gem.sku)
      if (new Set(skus).size !== skus.length) {
        error = i18n.t('errors.appNotification')
        errorBody = i18n.t('errors.duplicateGem')
      } else {
        const resp = await simpleStore.ajax(
          'POST',
          `ds/${searchType}`,
          JSON.stringify({
            request: {
              filters: {
                sku: skus
              }
            }
          })
        )
        if (resp.success) {
          if (skus.length == resp.payload.metadata.total_count) {
            isAvailable = true
            gems = resp.payload.gems
          } else {
            const filtersSkus = new Set(resp.payload.filters.sku)
            const gemsSkus = new Set(
              resp.payload.gems.map((item: { [key: string]: any }) => item.sku)
            )
            error = i18n.t('errors.diamondSold')
            errorBody = i18n.t('errors.diamondsNotAvailable', {
              skus: [...filtersSkus].filter(sku => !gemsSkus.has(sku)).join(',')
            })
          }
        } else {
          error = i18n.t('errors.applicationError')
          errorBody = i18n.t('errors.addToCartRequestError')
        }
      }
    } catch (e) {
      logger.info(e)
      error = i18n.t('errors.applicationError')
      errorBody = i18n.t('errors.addToCartRequestError')
    }
    return {
      isAvailable,
      gems,
      error,
      errorBody
    }
  }

  async findShopifyVariantId(options: any[], productId: any): Promise<any> {
    try {
      const result = await simpleStore.ajax('POST', `ds/find_variants_by_options`, JSON.stringify({options, productId}))
      return result;
    } catch (e) {
      logger.info('error getVariants:' + e)
      return null
    }
  }
  
  async doAddToCart(
    diamondProductsData: { [key: string]: any }[],
    productData: {
      variantId: string | null
      ringSize: string | null
    } | {}
  ): Promise<DiamondProcessResponse> {
    try {
      const items: addToCartItem[] = diamondProductsData.flatMap(diamondProduct => {
        const id = Object.keys(diamondProduct)[0]
        if (!id) return []
        const diamond = diamondProduct[id]
        if (!diamond) return []
        const matchResult = id.toString().match(/\d+$/)
        return [{
          id: matchResult ? parseInt(matchResult[0]) : 0,
          quantity: 1,
          properties: {
            Certificate: `${diamond.lab ?? ''} ${diamond.cert_num ?? ''}`.trim()
          }
        }]
      })

      if (items.length !== diamondProductsData.length) {
        return {
          success: false,
          error: {
            errorTitle: i18n.t('errors.invalidCartData'),
            errorBody: i18n.t('errors.invalidCartDataBody')
          }
        }
      }

      if ('variantId' in productData && productData.variantId) {
        let variantId = productData.variantId;
        const variantData = await this.getVariantById(variantId)
        const diamondData = diamondProductsData[0];
        const id = diamondData ? Object.keys(diamondData)[0] : undefined
        const diamondShape = diamondData && id ? diamondData[id]?.shape : undefined
        const productShapeOption: { name: string; value: string } | undefined = variantData?.selectedOptions?.find(
          (option: { name: string; value: string }) => option.name === "Shape"
        );
        if (productShapeOption && typeof diamondShape === 'string' && diamondShape != productShapeOption?.value?.toLowerCase()) {
          const selectedOptions: { name: string; value: string }[] = variantData?.selectedOptions ?? []
          const shapeIndex = selectedOptions.findIndex((option: { name: string }) => option.name === "Shape");
          if (shapeIndex >= 0) {
            const currentShapeOption = selectedOptions[shapeIndex]
            if (!currentShapeOption) {
              return {
                success: false,
                error: {
                  errorTitle: i18n.t('errors.addToCartError'),
                  errorBody: i18n.t('errors.unableToAddToCart'),
                  errorCode: null
                }
              }
            }
            const options = [
              ...selectedOptions.slice(0, shapeIndex),
              { ...currentShapeOption, value: diamondShape.charAt(0).toUpperCase() + diamondShape.slice(1) },
              ...selectedOptions.slice(shapeIndex + 1)
            ];
            variantId = await this.findShopifyVariantId(options, variantData?.product?.id);
          }
        }
        const grouping = Date.now().toString(26)
        items.forEach(item => (item.properties['_grouping'] = grouping));
        const parsedVariantId = parseInt(String(variantId), 10)
        const jewelryProduct: addToCartItem = {
          id: Number.isNaN(parsedVariantId) ? 0 : parsedVariantId,
          quantity: 1,
          properties: {
            _grouping: grouping
          }
        }
        if (productData?.ringSize) {
          jewelryProduct.properties['Ring Size'] = productData.ringSize
        }
        items.push(jewelryProduct)
      }
      const data = await simpleStore.ajax(
        'POST',
        '/cart/add.js',
        JSON.stringify({
          items
        }),
        undefined,
        undefined,
        { returnErrorResponse: true }
      )
      if (data?.items === undefined) {
        const errorMessage = data?.description || data?.message || i18n.t('errors.unableToAddToCart')
        return {
          success: false,
          error: {
            errorTitle: i18n.t('errors.addToCartError'),
            errorBody: errorMessage,
            errorCode: data?.status === 422 ? 'CART_422' : null
          }
        }
      }

      return {
        success: true
      }
    } catch (e) {
      logger.info(e)
      return {
        success: false,
        error: {
          errorTitle: i18n.t('errors.addToCartError'),
          errorBody: i18n.t('errors.unableToAddToCart'),
          errorCode: null
        }
      }
    }
  }

  addToCart = async (diamonds: { [key: string]: any }[], productData = {}, searchType = "diamond_search", stoneType = "diamond"): Promise<DiamondProcessResponse> => {
    try {
      let errorTitle = null
      let errorBody = null
      const gems = Array.isArray(diamonds) ? diamonds : [diamonds]
      const availabilityResult = await this.gemsAvailable(gems, searchType)
      if (availabilityResult.isAvailable) {
        const diamondsInCart = await this.checkDiamondInCart(availabilityResult.gems)
        if (diamondsInCart.length) {
          return {
            success: false,
            error: {
              errorTitle: i18n.t('errors.appNotification'),
              errorBody: i18n.t('errors.diamondAlreadyInCart'),
              errorCode: 'DIAMOND_ALREADY_IN_CART'
            }
          }
        }

        const htoCartItems = await simpleStore.ajax('GET', '/cart.js')
        if (!htoCartItems) {
          return {
            success: false,
            error: {
              errorTitle: i18n.t('errors.cartUnavailable'),
              errorBody: i18n.t('errors.cartUnavailableBody'),
              errorCode: 'HTO_CART_FETCH_ERROR'
            }
          }
        }
        const hasHtoItems = (htoCartItems.items ?? []).some(
          (item: { selling_plan_allocation?: unknown; properties?: Record<string, string> | Array<{ name: string; value: string }> }) => {
            if (Array.isArray(item.properties)) {
              if (item.properties.some((p) => p.name === '_ub_hto_kit' && p.value === 'true')) return true;
              return !!(item.selling_plan_allocation && item.properties.some((p) => p.name === '_ub_hto_product' && p.value === 'true'));
            }
            const props = item.properties as Record<string, string>;
            if (props?.['_ub_hto_kit'] === 'true') return true;
            return !!(item.selling_plan_allocation && props?.['_ub_hto_product'] === 'true');
          }
        )
        if (hasHtoItems) {
          return {
            success: false,
            error: {
              errorTitle: i18n.t('errors.htoKitActive'),
              errorBody: i18n.t('errors.htoKitActiveBody'),
              errorCode: 'HTO_CART_CONFLICT'
            }
          }
        }

        const { products, unprocessedSkus } = await this.buildDiamondProducts(availabilityResult.gems, searchType, stoneType)
        if (unprocessedSkus.length == 0) {
          const productVariantIds = products
            .map(product => {
              const id = Object.keys(product)[0]
              const matchResult = id?.toString().match(/\d+$/)
              return matchResult ? parseInt(matchResult[0]) : 0
            })
            .filter(id => id > 0)

          const retryDelaysMs = [1000, 2000, 3000]
          let lastAddToCartResult: DiamondProcessResponse | null = null
          for (const delay of retryDelaysMs) {
            await this.sleep(delay)
            const attemptResult = await this.doAddToCart(products, productData)
            if (attemptResult.success) {
              return { success: true }
            }
            lastAddToCartResult = attemptResult
            if (attemptResult.error?.errorCode !== 'CART_422') {
              break
            }
          }

          if (lastAddToCartResult?.error?.errorCode === 'CART_422') {
            const [retryAvailabilityResult, storeAvailability] = await Promise.all([
              this.gemsAvailable(gems, searchType),
              this.areVariantsAvailableInStore(productVariantIds)
            ])
            if (retryAvailabilityResult.isAvailable && storeAvailability) {
              errorTitle = i18n.t('errors.appNotification')
              errorBody = i18n.t('errors.tryAgainInAMoment')
            } else {
              errorTitle = lastAddToCartResult.error?.errorTitle
              errorBody = lastAddToCartResult.error?.errorBody
            }
          } else {
            errorTitle = lastAddToCartResult?.error?.errorTitle || i18n.t('errors.addToCartError')
            errorBody = lastAddToCartResult?.error?.errorBody || i18n.t('errors.unableToAddToCart')
          }
        } else {
          errorTitle = i18n.t('errors.createProductsError')
          errorBody = i18n.t('errors.unableToCreateProductsFor', { skus: unprocessedSkus.join(', ') })
        }
      } else {
        errorTitle = availabilityResult.error
        errorBody = availabilityResult.errorBody
      }
      return {
        success: false,
        error: {
          errorTitle,
          errorBody
        }
      }
    } catch (e) {
      logger.info(e)
      const errorMessage = e instanceof Error ? e.message : String(e)
      return {
        success: false,
        error: {
          errorTitle: i18n.t('errors.orderProcessError'),
          errorBody: errorMessage
        }
      }
    }
  }

  mergeOverridesIntoFilters = (filters: any, overrides: any, searchFiltersRef: any): any => {
    const updatedFilters = structuredClone(filters);
    const appliedFilters = searchFiltersRef.current;
    if (overrides.ratio?.filter) {
      if (overrides.ratio.filter.minValue !== undefined && appliedFilters?.ratio_min === undefined) {
        updatedFilters.filters.ratio_min = overrides.ratio.filter.minValue;
      }
      if (overrides.ratio.filter.maxValue !== undefined && appliedFilters?.ratio_max === undefined) {
        updatedFilters.filters.ratio_max = overrides.ratio.filter.maxValue;
      }
    }
    if (overrides.carat?.filter) {
      if (overrides.carat.filter.minValue !== undefined && appliedFilters?.weight_min === undefined) {
        updatedFilters.filters.weight_min = overrides.carat.filter.minValue;
      }
      if (overrides.carat.filter.maxValue !== undefined && appliedFilters?.weight_max === undefined) {
        updatedFilters.filters.weight_max = overrides.carat.filter.maxValue;
      }
    }
    if (overrides.shapes?.filter?.brilliant?.length && appliedFilters?.cut_code_shape === undefined) {
      updatedFilters.filters.cut_code_shape = overrides.shapes.filter.brilliant.flatMap((shape: string) => [
        `brilliant_cut:${shape}`,
        `modified_cut:${shape}`
      ]);
    }
    delete updatedFilters.filters.shape;
    return updatedFilters;
  }

  maskEmail = (email: unknown): string => {
    if (typeof email !== 'string') return ''
    return email.replace(/([\w.]*)([\w.]{2})(@)([a-zA-Z.]*)/, '******$2$3$4')
  }

  async sendEmail(
    to: string | null,
    subject: string,
    message: string | { text?: string; html?: string },
    cc?: string | null,
    bcc?: string | null,
    shop?: string | null
  ): Promise<{ success: boolean; error?: string }> {
    try {
      const data = await simpleStore.ajax(
        'POST',
        'send-email',
        JSON.stringify({
          to,
          subject,
          message,
          cc,
          bcc,
          shop
        })
      )
      if (data?.success) {
        return { success: true }
      }
      return {
        success: false,
        error: data?.error || i18n.t('errors.failedToSendEmail')
      }
    } catch (e) {
      logger.info('error sendEmail:' + e)
      return {
        success: false,
        error: i18n.t('errors.failedToSendEmail')
      }
    }
  }
  //TODO: remove roles length check. We assume no role = admin for now
  getUserRole = (): string => {
    const userData = JSON.parse(localStorage.getItem(USER_COOKIE_NAME) || '{}')
    return userData?.user?.roles?.includes(UserRoles.MerchantAdmin) || userData?.user?.is_owner || userData?.user?.roles?.length === 0 ? Roles.Merchant : Roles.Customer
  }

  isMemoMode = (): boolean => {
    const userData = JSON.parse(localStorage.getItem(USER_COOKIE_NAME) || '{}')
    return userData?.user ? true : false
  }

  getUser = (): { [key: string]: any } => {
    return JSON.parse(localStorage.getItem(USER_COOKIE_NAME) || '{}');
  }
}

export default StoreFunctions
